import React from 'react';
import {
  Upload, Sparkles, FileSearch, CheckSquare, CheckCircle2, Send, XCircle, Clock, Archive
} from 'lucide-react';

/**
 * DocumentProgressTimeline
 *
 * Shows a horizontal stepper for where a document is in the workflow.
 * Supports Invoice, Expense, and PurchaseOrder document types.
 *
 * Props:
 *  status       – current document status string (e.g. 'Pending Approval', 'Approved')
 *  approvals    – array of approval objects (optional)
 *  documentType – 'Invoice' | 'Expense' | 'PurchaseOrder' | 'CreditNote'
 *  isRejected   – boolean (alternate terminal state)
 *  className    – extra wrapper classes
 */

// ── Step definitions ───────────────────────────────────────────────────────────
const INVOICE_STEPS = [
  { key: 'uploaded',   label: 'Uploaded',        icon: Upload },
  { key: 'extracted',  label: 'AI Extracted',     icon: Sparkles },
  { key: 'review',     label: 'Finance Review',   icon: FileSearch },
  { key: 'approval',   label: 'Dept. Approval',   icon: CheckSquare },
  { key: 'approved',   label: 'Approved',         icon: CheckCircle2 },
  { key: 'exported',   label: 'Post to Finance',  icon: Send },
];

const EXPENSE_STEPS = [
  { key: 'draft',      label: 'Created',          icon: Upload },
  { key: 'submitted',  label: 'Submitted',        icon: Send },
  { key: 'review',     label: 'Under Review',     icon: FileSearch },
  { key: 'approved',   label: 'Approved',         icon: CheckCircle2 },
  { key: 'paid',       label: 'Paid',             icon: CheckSquare },
];

const PO_STEPS = [
  { key: 'draft',      label: 'Draft',            icon: Upload },
  { key: 'pending',    label: 'Pending Approval', icon: Clock },
  { key: 'approved',   label: 'Approved',         icon: CheckCircle2 },
  { key: 'invoiced',   label: 'Invoiced',         icon: FileSearch },
  { key: 'closed',     label: 'Closed',           icon: CheckSquare },
];

// ── Status → active step mapping ──────────────────────────────────────────────
function getActiveStep(status, documentType, isRejected) {
  const s = (status || '').toLowerCase().replace(/_/g, ' ');
  if (isRejected || s === 'rejected') return -1; // special rejected state

  if (documentType === 'Expense') {
    if (s === 'draft') return 0;
    if (s === 'submitted' || s === 'pending') return 1;
    if (s.includes('review') || s === 'pending approval') return 2;
    if (s === 'approved') return 3;
    if (s === 'paid' || s === 'posted') return 4;
    return 0;
  }

  if (documentType === 'PurchaseOrder') {
    if (s === 'draft' || s === 'new') return 0;
    if (s === 'pending' || s === 'pending approval') return 1;
    if (s === 'approved') return 2;
    if (s === 'invoiced' || s === 'partially invoiced') return 3;
    if (s === 'closed' || s === 'posted') return 4;
    return 0;
  }

  // Invoice (default)
  if (s === 'new' || s === 'draft') return 1; // Uploaded + AI extracted but not reviewed
  if (s === 'pending' || s === 'coding') return 2; // Finance reviewing
  if (s === 'pending approval') return 3; // Dept approval
  if (s === 'approved') return 4;
  if (s === 'posted' || s === 'exported') return 5;
  return 1;
}

function getSteps(documentType) {
  if (documentType === 'Expense') return EXPENSE_STEPS;
  if (documentType === 'PurchaseOrder') return PO_STEPS;
  return INVOICE_STEPS;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DocumentProgressTimeline({
  status,
  approvals = [],
  documentType = 'Invoice',
  className = '',
}) {
  const isRejected = (status || '').toLowerCase() === 'rejected';
  const steps = getSteps(documentType);
  const activeStep = getActiveStep(status, documentType, isRejected);

  // Count completed approval stages for the approval step tooltip
  const completedApprovals = approvals.filter(
    a => a.Status === 'Approved' || a.ApprovalStatus === 'Approved'
  ).length;
  const totalApprovals = approvals.length;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4 ${className}`}>
      <div className="flex items-center justify-between relative">
        {/* Connecting line */}
        <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-200 mx-10 z-0" />

        {steps.map((step, idx) => {
          const StepIcon = step.icon;
          const isCompleted = !isRejected && idx < activeStep;
          const isCurrent = !isRejected && idx === activeStep;
          const isPending = isRejected ? false : idx > activeStep;

          // Approval step: show progress badge
          const isApprovalStep =
            (documentType === 'Invoice' && step.key === 'approval') ||
            (documentType === 'Expense' && step.key === 'review') ||
            (documentType === 'PurchaseOrder' && step.key === 'pending');

          const badgeLabel = isApprovalStep && totalApprovals > 0
            ? `${completedApprovals}/${totalApprovals}`
            : null;

          return (
            <div key={step.key} className="flex flex-col items-center z-10 relative" style={{ flex: 1 }}>
              {/* Circle */}
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                  ${isCompleted
                    ? 'bg-green-500 border-green-500 text-white shadow-sm shadow-green-200'
                    : isCurrent
                    ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200 ring-4 ring-blue-100'
                    : isRejected && idx === activeStep + 1
                    ? 'bg-red-500 border-red-500 text-white shadow-sm shadow-red-200'
                    : 'bg-white border-gray-300 text-gray-400'
                  }
                `}
                title={step.label}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : isRejected && idx === 0 ? (
                  <XCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <StepIcon className="w-4.5 h-4.5" />
                )}
              </div>

              {/* Badge (approval count) */}
              {badgeLabel && (isCurrent || isCompleted) && (
                <div
                  className={`absolute -top-1.5 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center
                    ${isCompleted ? 'bg-green-600 text-white' : 'bg-blue-700 text-white'}`}
                >
                  {badgeLabel}
                </div>
              )}

              {/* Label */}
              <span
                className={`mt-2 text-xs font-medium text-center leading-tight ${
                  isCurrent
                    ? 'text-blue-700'
                    : isCompleted
                    ? 'text-green-600'
                    : isRejected && idx < 2
                    ? 'text-red-500'
                    : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}

        {/* Rejected overlay label */}
        {isRejected && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              <XCircle className="w-3.5 h-3.5" />
              Rejected
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
